import { useState, useCallback, useEffect } from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { Menu, Bell } from 'lucide-react'
import { UserButton } from '@clerk/clerk-react'
import { DashboardSidebar } from './DashboardSidebar'
import { RouteErrorBoundary } from '@/components/ErrorBoundary'
import { Toaster } from '@/components/ui/toaster'
import { useNotificationCount } from '@/hooks/useNotificationCount'
import { useDashboardSSE } from '@/hooks/useDashboardSSE'
import type { SSEConnectionStatus } from '@/lib/sse'

/**
 * Routes that render edge-to-edge: no page padding and no `max-w-7xl` cap, so
 * the view gets the full width of the main area (viewport minus the sidebar).
 * The Loom's force graph sizes itself from its container, so capping it at
 * 1280px left dead space on wide screens.
 */
const FULL_BLEED_ROUTES = new Set(['/dashboard/loom'])

/** Map route paths to page titles */
const PAGE_TITLES: Record<string, string> = {
  '/dashboard/zeus': 'ZEUS · COUNCIL CEO',
  '/dashboard': 'COMMAND CENTRE',
  '/dashboard/decisions': 'DECISION QUEUE',
  '/dashboard/activity': 'ACTIVITY LOG',
  '/dashboard/agents': 'AGENT DIRECTORY · COUNCIL + GUARDIANS',
  '/dashboard/loom': 'THE LOOM · AGENT KNOWLEDGE GRAPH',
  '/dashboard/customers': 'CUSTOMERS',
  '/dashboard/costs': 'COSTS',
  '/dashboard/health': 'HEALTH',
}

/** Resolve the top-bar title for a path, handling dynamic detail routes. */
function resolvePageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname]
  // Customer detail (/dashboard/customers/:tenantId) keeps the section title.
  if (pathname.startsWith('/dashboard/customers/')) return 'CUSTOMER'
  return 'DASHBOARD'
}

/** Small live-region dot reflecting the real-time SSE connection state. */
function LiveStatusDot({ status }: { status: SSEConnectionStatus }) {
  const config: Record<SSEConnectionStatus, { color: string; label: string }> = {
    connected: { color: 'bg-emerald-500', label: 'Live updates connected' },
    connecting: { color: 'bg-amber-500 animate-pulse', label: 'Connecting to live updates' },
    reconnecting: { color: 'bg-amber-500 animate-pulse', label: 'Reconnecting to live updates' },
    error: { color: 'bg-cmd-error', label: 'Live updates disconnected' },
    closed: { color: 'bg-cmd-outline', label: 'Live updates closed' },
  }
  const { color, label } = config[status]
  return (
    <span className="flex items-center" title={label}>
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} aria-hidden="true" />
      <span className="sr-only" role="status" aria-live="polite">
        {label}
      </span>
    </span>
  )
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Apply dark theme to <html> and <body> for dashboard routes
  useEffect(() => {
    document.documentElement.classList.add('dark')
    document.body.classList.add('bg-cmd-base')
    return () => {
      document.documentElement.classList.remove('dark')
      document.body.classList.remove('bg-cmd-base')
    }
  }, [])

  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const pageTitle = resolvePageTitle(location.pathname)
  const fullBleed = FULL_BLEED_ROUTES.has(location.pathname)
  const { total } = useNotificationCount()

  // P1-1: open the single dashboard-wide SSE connection so agent
  // `update_dashboard_widget` broadcasts reach the widgets in real time.
  const sseStatus = useDashboardSSE()

  return (
    <div className="min-h-screen bg-cmd-base text-white/90">
      {/* Skip to content */}
      <a
        href="#dashboard-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-cmd-container focus:px-4 focus:py-2 focus:text-primary focus:outline-none"
      >
        Skip to content
      </a>

      <DashboardSidebar open={sidebarOpen} onClose={closeSidebar} />

      {/* Main area (offset by sidebar width on desktop) */}
      <div className="lg:ml-64 flex min-h-screen flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-cmd-low px-4 sm:px-6">
          {/* Left: hamburger (mobile) + title */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 -ml-1.5 text-cmd-outline hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
              aria-controls="dashboard-sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="font-sans text-xs font-semibold tracking-widest text-cmd-outline uppercase">
              {pageTitle}
            </h1>
            <LiveStatusDot status={sseStatus} />
          </div>

          {/* Right: notification bell + avatar */}
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/decisions"
              className="relative p-1.5 text-cmd-outline hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label={total > 0 ? `Notifications — ${total} need attention` : 'Notifications'}
            >
              <Bell className="h-5 w-5" />
              {total > 0 && (
                <span
                  className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-cmd-error px-1 font-mono text-[0.55rem] font-bold tabular-nums text-white"
                  aria-hidden="true"
                >
                  {total > 99 ? '99+' : total}
                </span>
              )}
            </Link>
            <UserButton
              afterSignOutUrl="/sign-in"
              appearance={{ elements: { avatarBox: 'h-8 w-8' } }}
            />
          </div>
        </header>

        {/* Content */}
        <main id="dashboard-content" className={fullBleed ? 'flex-1' : 'flex-1 p-4 sm:p-6'}>
          <div className={fullBleed ? '' : 'mx-auto max-w-7xl'}>
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>

      {/* Global toast stack — confirms decision approve/reject across the app */}
      <Toaster />
    </div>
  )
}
